<template>
  <div class="platform-box" ref="myElementRef">
    <el-alert
      class="tip-bar"
      type="info"
      :closable="false"
      :title="t('platformRenewal.tip')"
    />
    <div ref="searchBox">
      <hc-search :data="searchData" @serch="getSearch" />
    </div>

    <hc-table
      :tableData="state.tableData"
      :column="state.tableHeader"
      :offsetHeight="state.offsetHeight"
      :maxPage="maxPage"
      @pageChange="pageChange"
      @limitChange="limitChange"
    >
      <template #status="{ row }">
        <el-tag :type="row.status == 1 ? 'success' : 'info'">{{ row.status == 1 ? t('platformAdmin.enable') : t('platformAdmin.disable') }}</el-tag>
      </template>
      <template #subscription_years="{ row }">
        {{ formatSubscriptionYears(row.subscription_years) }}
      </template>
      <template #opened_at="{ row }">{{ formatDate(row.opened_at) }}</template>
      <template #expire_at="{ row }">
        {{ formatDate(row.expire_at) }}
        <el-tag v-if="isExpired(row)" type="danger" size="small" class="exp-tag">{{ t('platformAdmin.expired') }}</el-tag>
        <el-tag v-else-if="!row.opened_at" type="warning" size="small" class="exp-tag">{{ t('platformAdmin.notOpened') }}</el-tag>
      </template>
      <template #operation="{ row }">
        <el-button size="small" link type="primary" @click="openRenew(row)">
          {{ row.opened_at ? t('platformRenewal.renew') : t('platformRenewal.open') }}
        </el-button>
      </template>
    </hc-table>

    <el-dialog v-model="renewVisible" :title="renewForm.opened_at ? t('platformRenewal.renewTitle') : t('platformRenewal.openTitle')" width="560px">
      <el-form ref="renewFormRef" :model="renewForm" :rules="renewRules" label-width="120px">
        <el-form-item :label="t('platformRenewal.tenant')">
          <span>{{ renewForm.tenant_name }}（{{ renewForm.tenant_code }}）</span>
        </el-form-item>
        <el-form-item :label="t('platformRenewal.currentExpire')">
          <span>{{ formatDate(renewForm.expire_at) }}</span>
        </el-form-item>
        <el-form-item :label="t('platformRenewal.durationType')" prop="duration_type">
          <el-radio-group v-model="renewForm.duration_type" @change="onDurationTypeChange">
            <el-radio label="month">{{ t('platformRenewal.byMonth') }}</el-radio>
            <el-radio label="year">{{ t('platformRenewal.byYear') }}</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="renewForm.duration_type === 'month'" :label="t('platformRenewal.months')" prop="subscription_months">
          <el-radio-group v-model="renewForm.subscription_months">
            <el-radio-button :label="1">{{ t('platformRenewal.month1') }}</el-radio-button>
            <el-radio-button :label="3">{{ t('platformRenewal.month3') }}</el-radio-button>
            <el-radio-button :label="6">{{ t('platformRenewal.month6') }}</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-else :label="t('platformRenewal.years')" prop="subscription_years">
          <el-select v-model="renewForm.subscription_years" style="width: 100%">
            <el-option v-for="y in [1, 2, 3, 4, 5]" :key="y" :label="y + t('platformRenewal.yearSuffix')" :value="y" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="renewForm.opened_at" :label="t('platformRenewal.expireCalc')">
          <el-checkbox v-model="renewForm.renew_from_today">{{ t('platformRenewal.renewFromToday') }}</el-checkbox>
          <p class="field-hint">
            {{ renewForm.duration_type === 'month'
              ? t('platformRenewal.hintMonth')
              : t('platformRenewal.hintYear') }}
          </p>
        </el-form-item>
        <el-form-item :label="t('platformRenewal.remark')">
          <el-input v-model="renewForm.remark" type="textarea" rows="2" :placeholder="t('platformRenewal.remarkPh')" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="renewVisible = false">{{ t('common.cancel') }}</el-button>
        <el-button type="primary" @click="submitRenew">{{ t('common.confirm') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted, nextTick } from 'vue'
import hcSearch from '@/components/hc-search/index.vue'
import hcTable from '@/components/hc-table/index.vue'
import { tenantRenewListApi, tenantRenewApi } from '@/api/platform/tenants'
import { ElMessage, ElMessageBox } from 'element-plus'
import { usePageI18n } from '@/composables/usePageI18n.js'
import { applySearchLabels, applyColumnLabels } from '@/utils/listPageI18n.js'

const RENEW_COLUMNS = {
  tenant_code: 'platformRenewal.colTenantCode',
  tenant_name: 'platformRenewal.colTenantName',
  subscription_years: 'platformRenewal.colYears',
  opened_at: 'platformRenewal.colOpenedAt',
  expire_at: 'platformRenewal.colExpireAt',
  status: 'platformRenewal.colStatus',
  operation: 'platformRenewal.colOperation',
}

const myElementRef = ref()
const searchBox = ref()
const renewFormRef = ref()
const maxPage = ref(0)
const page = ref(1)
const limit = ref(20)
const renewVisible = ref(false)

const searchData = reactive([
  { label: '关键词', type: 'input', value: '', field: 'keywords', placeholder: '租户码/名称' }
])

const renewForm = reactive({
  id: null,
  tenant_code: '',
  tenant_name: '',
  opened_at: '',
  expire_at: '',
  duration_type: 'month',
  subscription_months: 1,
  subscription_years: 1,
  renew_from_today: false,
  remark: ''
})

const state = reactive({
  offsetHeight: 0,
  tableData: [],
  tableHeader: [],
  column: [
    { type: 'index', label: '序号', width: 70, show: true },
    { prop: 'tenant_code', label: '租户码', width: 100, show: true },
    { prop: 'tenant_name', label: '租户名称', width: 140, show: true },
    { prop: 'subscription_years', label: '当前年限', width: 90, show: true },
    { prop: 'opened_at', label: '开通时间', width: 160, show: true },
    { prop: 'expire_at', label: '到期时间', width: 200, show: true },
    { prop: 'status', label: '状态', width: 80, show: true },
    { prop: 'operation', label: '操作', width: 90, show: true }
  ]
})

const renewRules = reactive({
  duration_type: [{ required: true, message: '', trigger: 'change' }],
  subscription_months: [{ validator: (_r, _v, cb) => cb(), trigger: 'change' }],
  subscription_years: [{ validator: (_r, _v, cb) => cb(), trigger: 'change' }],
})

const applyLabels = () => {
  applySearchLabels(searchData, {
    keywords: { labelKey: 'platformRenewal.keywords', placeholderKey: 'platformRenewal.keywordsPh' },
  })
  applyColumnLabels(state.column, RENEW_COLUMNS)
  renewRules.duration_type[0].message = t('platformRenewal.selectDuration')
  renewRules.subscription_months[0].validator = (_rule, _value, callback) => {
    if (renewForm.duration_type === 'month' && ![1, 3, 6].includes(renewForm.subscription_months)) {
      callback(new Error(t('platformRenewal.selectMonths')))
    } else {
      callback()
    }
  }
  renewRules.subscription_years[0].validator = (_rule, _value, callback) => {
    if (renewForm.duration_type === 'year' && (!renewForm.subscription_years || renewForm.subscription_years < 1)) {
      callback(new Error(t('platformRenewal.selectYears')))
    } else {
      callback()
    }
  }
}
const { t } = usePageI18n(applyLabels)

const formatDate = (val) => {
  if (!val) return '—'
  const s = String(val)
  return s.length >= 16 ? s.substring(0, 16) : s
}

const formatSubscriptionYears = (years) => {
  if (years == null || years === '' || Number(years) === 0) return t('platformRenewal.monthlyOrUnset')
  return years + t('platformAdmin.yearsSuffix')
}

const isExpired = (row) => {
  if (!row.expire_at) return false
  return new Date(row.expire_at) < new Date()
}

const tableHeaderFn = () => {
  state.tableHeader = state.column.filter((v) => v.show)
}

const offHeighths = () => {
  if (myElementRef.value) {
    state.offsetHeight = myElementRef.value.offsetHeight - 120 - (searchBox.value?.offsetHeight || 0)
  }
}

const onDurationTypeChange = () => {
  if (renewForm.duration_type === 'month') {
    renewForm.subscription_months = 1
    if (renewForm.opened_at) {
      renewForm.renew_from_today = false
    }
  } else {
    renewForm.subscription_years = 1
  }
}

const openRenew = (row) => {
  renewForm.id = row.id
  renewForm.tenant_code = row.tenant_code
  renewForm.tenant_name = row.tenant_name
  renewForm.opened_at = row.opened_at || ''
  renewForm.expire_at = row.expire_at || ''
  renewForm.duration_type = row.opened_at ? 'month' : 'year'
  renewForm.subscription_months = 1
  renewForm.subscription_years = row.subscription_years ? Number(row.subscription_years) : 1
  renewForm.renew_from_today = row.opened_at ? false : true
  renewForm.remark = ''
  renewVisible.value = true
}

const getSearch = (params) => {
  tenantRenewListApi({
    page: page.value,
    limit: limit.value,
    keywords: params?.keywords ?? searchData[0].value ?? ''
  }).then((res) => {
    if (res.code === 200) {
      state.tableData = res.data || []
      maxPage.value = res.maxPage || 0
    } else {
      ElMessage.error(res.msg || '加载失败')
    }
  })
}

const pageChange = (p) => {
  page.value = p
  getSearch()
}

const limitChange = (l) => {
  limit.value = l
  page.value = 1
  getSearch()
}

const buildDurationLabel = () => {
  if (renewForm.duration_type === 'month') {
    return `${renewForm.subscription_months} 个月`
  }
  return `${renewForm.subscription_years} 年`
}

const submitRenew = () => {
  renewFormRef.value.validate((valid) => {
    if (!valid) return
    const action = renewForm.opened_at ? '续期' : '开通'
    const durationLabel = buildDurationLabel()
    ElMessageBox.confirm(
      `确认为租户「${renewForm.tenant_name}」${action} ${durationLabel}？`,
      '提示',
      { type: 'warning' }
    )
      .then(() => {
        const payload = {
          id: renewForm.id,
          duration_type: renewForm.duration_type,
          renew_from_today: renewForm.renew_from_today,
          remark: renewForm.remark
        }
        if (renewForm.duration_type === 'month') {
          payload.subscription_months = renewForm.subscription_months
        } else {
          payload.subscription_years = renewForm.subscription_years
        }
        tenantRenewApi(payload).then((res) => {
          if (res.code === 200) {
            ElMessage.success(res.msg || '操作成功')
            renewVisible.value = false
            getSearch()
          } else {
            ElMessage.error(res.msg || '操作失败')
          }
        })
      })
      .catch(() => {})
  })
}

onMounted(() => {
  applyLabels()
  tableHeaderFn()
  nextTick(() => {
    offHeighths()
    getSearch()
  })
})
</script>

<style lang="less" scoped>
.platform-box {
  flex: 1;
  overflow: auto;
  background: #fff;
  border-radius: 6px;
  padding: 10px;
  margin-right: 10px;
}
.tip-bar {
  margin-bottom: 12px;
}
.field-hint {
  margin: 4px 0 0 0;
  font-size: 12px;
  color: #909399;
}
.exp-tag {
  margin-left: 6px;
}
</style>
